import React, { useState, useEffect } from 'react';
import Icon from './AppIcon';
import Button from './ui/Button';
import Image from './AppImage';
import { sendTip, getSavedPaymentMethods } from '../services/tipService';
import toast from 'react-hot-toast';

const PRESETS = [5, 10, 25, 50];

const brandIcon = (brand) => {
  const map = { visa: '💳', mastercard: '💳', amex: '💳', discover: '💳' };
  return map[brand?.toLowerCase()] || '💳';
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

/**
 * TipModal
 *
 * Props:
 *   expert       { id, full_name, title, avatar_url }
 *   contractId   UUID
 *   isOpen       boolean
 *   onClose      () => void
 *   onSubmitted  () => void
 */
const TipModal = ({ expert, contractId, isOpen, onClose, onSubmitted }) => {
  const [selected, setSelected]   = useState(null); // preset amount
  const [custom, setCustom]       = useState('');    // custom amount string
  const [message, setMessage]     = useState('');
  const [methods, setMethods]     = useState([]);
  const [chosenPm, setChosenPm]   = useState(null);  // stripe_payment_method_id
  const [loadingPm, setLoadingPm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent]           = useState(false);

  // Derived amount
  const amount = selected ?? (custom ? parseFloat(custom) : 0);
  const amountValid = amount >= 1 && amount <= 10000 && !isNaN(amount);

  useEffect(() => {
    if (!isOpen) return;
    setSent(false);
    setSelected(null);
    setCustom('');
    setMessage('');
    loadPaymentMethods();
  }, [isOpen]);

  const loadPaymentMethods = async () => {
    setLoadingPm(true);
    const result = await getSavedPaymentMethods();
    if (result.success) {
      setMethods(result.methods);
      const def = result.methods.find((m) => m.is_default) || result.methods[0] || null;
      setChosenPm(def?.stripe_payment_method_id ?? null);
    }
    setLoadingPm(false);
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handlePreset = (val) => {
    setSelected(val);
    setCustom('');
  };

  const handleCustom = (val) => {
    setCustom(val);
    setSelected(null);
  };

  const handleSubmit = async () => {
    if (!amountValid) { toast.error('Enter a valid tip amount (min $1)'); return; }
    if (!chosenPm) { toast.error('No payment method found. Add a card in Account Settings.'); return; }

    setSubmitting(true);
    try {
      const result = await sendTip({
        contractId,
        amount,
        paymentMethodId: chosenPm,
        message,
      });

      if (!result.success) throw new Error(result.error);

      setSent(true);
      onSubmitted?.();
    } catch (err) {
      toast.error(err?.message || 'Failed to send tip. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !expert || !contractId) return null;

  const inputClass =
    'w-full px-3 py-2 text-sm bg-background border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-ring transition-shadow';

  const defaultMethod = methods.find((m) => m.stripe_payment_method_id === chosenPm);

  return (
    <div
      className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />

      {/* Sheet */}
      <div className="relative z-10 bg-card w-full sm:max-w-md sm:mx-4 max-h-[95vh] sm:max-h-[80vh] rounded-t-3xl sm:rounded-2xl border border-border shadow-2xl flex flex-col overflow-hidden">
        {/* Mobile handle */}
        <div className="sm:hidden flex justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-foreground">Send a Tip</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Show your appreciation — 100% goes to the expert</p>
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-xl hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Close"
            disabled={submitting}
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Expert strip */}
        <div className="flex items-center gap-3 px-5 py-3 bg-amber-500/5 border-b border-border flex-shrink-0">
          {expert.avatar_url ? (
            <Image
              src={expert.avatar_url}
              alt={expert.full_name}
              className="w-10 h-10 rounded-xl object-cover ring-2 ring-border"
            />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={18} className="text-amber-500" />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-sm font-bold text-foreground truncate">{expert.full_name}</p>
            {expert.title && <p className="text-xs text-muted-foreground truncate">{expert.title}</p>}
          </div>
        </div>

        {/* Content */}
        {sent ? (
          /* ── Success state ── */
          <div className="flex-1 flex flex-col items-center justify-center px-5 py-10 text-center gap-4">
            <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center">
              <Icon name="Heart" size={32} className="text-amber-500" />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">Tip Sent!</p>
              <p className="text-sm text-muted-foreground mt-1">
                ${amount.toFixed(2)} has been sent to {expert.full_name}.<br />
                They'll see it in their Stripe balance.
              </p>
            </div>
            <Button variant="default" onClick={handleClose} iconName="Check" iconPosition="left">
              Done
            </Button>
          </div>
        ) : (
          /* ── Input state ── */
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
            {/* Preset amounts */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Choose an amount <span className="text-destructive">*</span>
              </label>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handlePreset(p)}
                    className={`py-2 rounded-xl text-sm font-bold border transition-colors ${
                      selected === p
                        ? 'bg-amber-500 text-white border-amber-500'
                        : 'bg-background border-border text-foreground hover:border-amber-400 hover:text-amber-600'
                    }`}
                  >
                    ${p}
                  </button>
                ))}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-medium">$</span>
                <input
                  type="number"
                  min="1"
                  max="10000"
                  step="1"
                  value={custom}
                  onChange={(e) => handleCustom(e.target.value)}
                  placeholder="Custom amount"
                  className={`${inputClass} pl-7 ${selected === null && custom ? 'border-amber-400 ring-amber-400/20' : ''}`}
                />
              </div>
            </div>

            {/* Thank-you message */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">
                Message <span className="text-xs text-muted-foreground font-normal">(optional)</span>
              </label>
              <textarea
                rows={2}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Thanks for the excellent work! Really appreciate your…"
                className={`${inputClass} resize-none`}
                maxLength={300}
              />
              <p className="text-xs text-muted-foreground text-right mt-0.5">{message.length}/300</p>
            </div>

            {/* Payment method */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-2">Payment method</label>
              {loadingPm ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Loader" size={15} className="animate-spin" />
                  Loading cards…
                </div>
              ) : methods.length === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-warning/10 border border-warning/30 rounded-xl text-sm text-warning">
                  <Icon name="AlertTriangle" size={15} />
                  No saved cards. Add one in Account Settings to send a tip.
                </div>
              ) : (
                <div className="space-y-2">
                  {methods.map((m) => (
                    <button
                      key={m.stripe_payment_method_id}
                      type="button"
                      onClick={() => setChosenPm(m.stripe_payment_method_id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                        chosenPm === m.stripe_payment_method_id
                          ? 'bg-primary/5 border-primary/40'
                          : 'bg-background border-border hover:border-primary/20'
                      }`}
                    >
                      <span className="text-xl leading-none">{brandIcon(m.card_brand)}</span>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground">
                          {capitalize(m.card_brand)} •••• {m.card_last4}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Expires {m.card_exp_month}/{m.card_exp_year}
                        </p>
                      </div>
                      {m.is_default && (
                        <span className="text-xs text-primary font-medium bg-primary/10 px-2 py-0.5 rounded-full">
                          Default
                        </span>
                      )}
                      {chosenPm === m.stripe_payment_method_id && (
                        <Icon name="CheckCircle" size={16} className="text-primary flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Disclaimer
            <div className="flex gap-2 p-3 bg-muted/50 border border-border rounded-xl">
              <Icon name="Info" size={14} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                Tips are processed immediately. 100% of the tip goes to the expert's Stripe balance — no platform fee.
              </p>
            </div> */}
          </div>
        )}

        {/* Footer */}
        {!sent && (
          <div className="flex items-center gap-3 px-5 py-4 border-t border-border bg-muted/20 flex-shrink-0">
            <Button variant="outline" className="sm:w-24" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="default"
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white border-amber-500"
              disabled={submitting || !amountValid || !chosenPm}
              onClick={handleSubmit}
            >
              {submitting
                ? 'Sending…'
                : amountValid
                  ? `Send $${amount.toFixed(2)} Tip`
                  : 'Enter an amount'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TipModal;
